import React, { useState, useRef, useEffect } from 'react';
import { Plus, Clock, RefreshCw, Briefcase, Coffee, History, CheckCircle, X, AlertTriangle } from 'lucide-react';
import { formatTime, formatDuration, getRestLabel, getRestLabelFull, getRestBadgeClasses, getRestLabelColor } from './formatters';
import CommonFields from './CommonFields';
import { checkInsertConflict } from './insertValidation';

const InsertButton = ({ segType, onInsertRest, onInsertWork, segId }) => {
  if (segType === 'WORK') {
    return (
      <button
        onClick={onInsertRest}
        className="p-2 rounded-lg flex items-center gap-1 text-xs font-medium bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
        title="Insert REST into this WORK"
        data-testid={`insert-into-${segId}`}
      >
        <Coffee className="w-3 h-3" />
        REST
      </button>
    );
  }
  return (
    <button
      onClick={onInsertWork}
      className="p-2 rounded-lg flex items-center gap-1 text-xs font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
      title="Insert WORK into this REST"
      data-testid={`insert-into-${segId}`}
    >
      <Briefcase className="w-3 h-3" />
      WORK
    </button>
  );
};

const InlineInsertForm = ({ mode, segDate, onSubmit, onCancel, segments }) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [date, setDate] = useState(segDate);
  const [reason, setReason] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const isWork = mode === 'insert_work';
  const borderColor = isWork ? 'border-blue-500/50' : 'border-emerald-500/50';
  const bgColor = isWork ? 'bg-blue-500/5' : 'bg-emerald-500/5';
  const accent = isWork ? 'text-blue-400' : 'text-emerald-400';

  // Pre-submission conflict check
  const segType = isWork ? 'WORK' : 'REST';
  const conflict = (startTime && endTime && date)
    ? checkInsertConflict(segments || [], segType, date, startTime, endTime)
    : null;

  const handleSubmit = async () => {
    if (!startTime || !endTime || !reason) return;
    setSubmitting(true);
    try {
      await onSubmit({ mode, date, startTime, endTime, reason, evidenceUrl });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEvidenceFile(file);
  };

  return (
    <div ref={formRef} className={`ml-0 mt-1 mb-2 rounded-xl border ${borderColor} ${bgColor} p-4 space-y-3`} data-testid="inline-insert-form">
      <div className="flex items-center justify-between">
        <p className={`text-sm font-semibold ${accent} flex items-center gap-1.5`}>
          {isWork ? <Briefcase className="w-4 h-4" /> : <Coffee className="w-4 h-4" />}
          Insert Missed {isWork ? 'Work' : 'Rest'}
        </p>
        <button onClick={onCancel} className="p-1 text-slate-400 hover:text-white" data-testid="inline-insert-cancel">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="inline-insert-date" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">Start</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="inline-insert-start" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">End</label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="inline-insert-end" />
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-slate-400 mb-1">Reason (required)</label>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Driver forgot to switch"
          className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500"
          data-testid="inline-insert-reason" />
      </div>

      {conflict && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="inline-insert-conflict">
          <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-red-300 text-xs">{conflict}</p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600"
          data-testid="inline-insert-cancel-btn">
          Cancel
        </button>
        <button onClick={handleSubmit}
          disabled={submitting || !startTime || !endTime || !reason || !!conflict}
          className={`px-3 py-1.5 rounded-lg text-sm text-white flex items-center gap-1.5 disabled:opacity-50 ${isWork ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          data-testid="inline-insert-submit">
          {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
          Insert
        </button>
      </div>
    </div>
  );
};

const InlineAdjustForm = ({ transition, initialTime, onSubmit, onCancel }) => {
  const [adjustTime, setAdjustTime] = useState(initialTime || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleSubmit = async () => {
    if (!adjustTime || !reason) return;
    setSubmitting(true);
    try {
      await onSubmit({ adjustTime, reason });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={formRef} className="ml-0 mt-1 mb-2 rounded-xl border border-amber-500/50 bg-amber-500/5 p-4 space-y-3" data-testid="inline-adjust-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-amber-400 flex items-center gap-1.5">
          <Clock className="w-4 h-4" />
          Adjust Crossover: {transition.transition_type}
        </p>
        <button onClick={onCancel} className="p-1 text-slate-400 hover:text-white" data-testid="inline-adjust-cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-slate-400 text-xs">Current: {formatTime(transition.current_time)}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">New Time</label>
          <input type="time" value={adjustTime} onChange={e => setAdjustTime(e.target.value)}
            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm [color-scheme:dark]"
            data-testid="inline-adjust-time" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-1">Reason (required)</label>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Driver forgot to switch"
            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500"
            data-testid="inline-adjust-reason" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600">
          Cancel
        </button>
        <button onClick={handleSubmit}
          disabled={submitting || !adjustTime || !reason}
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm text-white flex items-center gap-1.5 disabled:opacity-50"
          data-testid="inline-adjust-submit">
          {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
          Adjust
        </button>
      </div>
    </div>
  );
};

const SegmentTimeline = ({
  segments, loading, showStatutory,
  startDate, startTime, endDate, endTime,
  onRefresh, onToggleAuditLog, showAuditLog,
  onInsertWork, onInsertRest, onEditTransition,
  onInlineInsert, onInlineSubmit, inlineInsertSegId, inlineInsertMode, onCancelInlineInsert,
  inlineAdjustSegId, inlineAdjustTransition, inlineAdjustTime, onInlineAdjustSubmit, onCancelInlineAdjust
}) => {
  const handleEditClick = (seg, idx) => {
    const timeOnly = new Date(seg.end_ts).toLocaleTimeString('en-AU', {
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    const nextType = segments[idx + 1] ? segments[idx + 1].segment_type : 'REST';
    onEditTransition(seg.segment_id, {
      segment_id: seg.segment_id,
      current_time: seg.end_ts,
      transition_type: seg.segment_type + ' \u2192 ' + nextType
    }, timeOnly);
  };

  // Get the date from a segment's start_ts for pre-filling the inline form
  const getSegDate = (seg) => {
    try {
      return new Date(seg.start_ts).toISOString().split('T')[0];
    } catch {
      return startDate;
    }
  };

  const renderSegment = (seg, idx) => {
    const isWork = seg.segment_type === 'WORK';
    const badgeClass = isWork ? 'bg-blue-500/20 border-blue-500/30' : getRestBadgeClasses(seg);

    return (
      <div key={seg.segment_id}>
        <div className="flex items-center gap-3">
          <div className={'flex-1 p-3 rounded-lg border ' + badgeClass}>
            <div className="flex items-center justify-between">
              <span className="font-semibold flex items-center gap-2">
                {isWork ? (
                  <span className="text-blue-400">WORK</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <span className={'inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ' + getRestBadgeClasses(seg)}>
                      {getRestLabel(seg)}
                    </span>
                    <span className={'text-[11px] font-normal opacity-70 ' + getRestLabelColor(seg)}>{getRestLabelFull(seg)}</span>
                  </span>
                )}
                {seg.corrected && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded" title="Edited by record keeper">EDITED</span>
                )}
                {seg.source === 'RECORD_KEEPER_EDIT' && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">INSERTED</span>
                )}
              </span>
              <span className="text-slate-400 text-sm">
                {formatDuration(seg.duration_seconds, seg.end_ts, seg.segment_type, showStatutory)}
              </span>
            </div>
            <div className="text-white text-sm mt-1">
              {formatTime(seg.start_ts)} - {formatTime(seg.end_ts)}
            </div>
          </div>

          {seg.end_ts && (
            <div className="flex flex-col gap-1">
              <button
                onClick={() => handleEditClick(seg, idx)}
                className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30"
                title="Adjust crossover time"
                data-testid={'adjust-btn-' + seg.segment_id}
              >
                <Clock className="w-4 h-4" />
              </button>
              <InsertButton
                segType={seg.segment_type}
                onInsertRest={() => onInlineInsert(seg.segment_id, 'insert_rest', getSegDate(seg))}
                onInsertWork={() => onInlineInsert(seg.segment_id, 'insert_work', getSegDate(seg))}
                segId={seg.segment_id}
              />
            </div>
          )}
        </div>

        {/* Inline adjust form directly below this segment */}
        {inlineAdjustSegId === seg.segment_id && inlineAdjustTransition && (
          <InlineAdjustForm
            transition={inlineAdjustTransition}
            initialTime={inlineAdjustTime}
            onSubmit={onInlineAdjustSubmit}
            onCancel={onCancelInlineAdjust}
          />
        )}

        {/* Inline insert form directly below this segment */}
        {inlineInsertSegId === seg.segment_id && inlineInsertMode && (
          <InlineInsertForm
            mode={inlineInsertMode}
            segDate={getSegDate(seg)}
            onSubmit={onInlineSubmit}
            onCancel={onCancelInlineInsert}
            segments={segments}
          />
        )}
      </div>
    );
  };

  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">
          Segments: {startDate} {startTime} - {endDate === startDate ? '' : endDate + ' '}{endTime}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={onToggleAuditLog}
            className="px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg text-sm flex items-center gap-1 hover:bg-slate-600"
            data-testid="toggle-audit-log-btn"
          >
            <History className="w-4 h-4" />
            Audit Log
          </button>
          <div className="relative group">
            <button
              className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm flex items-center gap-1 hover:bg-emerald-500/30"
              data-testid="insert-missed-btn"
            >
              <Plus className="w-4 h-4" />
              Insert Missed Entry
            </button>
            <div className="absolute right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[180px]">
              <button
                onClick={onInsertWork}
                className="w-full px-4 py-3 text-left text-white hover:bg-slate-700 flex items-center gap-2 rounded-t-lg"
                data-testid="insert-missed-work-btn"
              >
                <Briefcase className="w-4 h-4 text-blue-400" />
                Missed Work Time
              </button>
              <button
                onClick={onInsertRest}
                className="w-full px-4 py-3 text-left text-white hover:bg-slate-700 flex items-center gap-2 rounded-b-lg"
                data-testid="insert-missed-rest-btn"
              >
                <Coffee className="w-4 h-4 text-emerald-400" />
                Missed Rest Break
              </button>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="p-1.5 text-slate-400 hover:text-white"
            disabled={loading}
            data-testid="refresh-segments-btn"
          >
            <RefreshCw className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
        </div>
      ) : segments.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No segments found for this date range</p>
      ) : (
        <div className="space-y-2">
          {segments.map((seg, idx) => renderSegment(seg, idx))}
        </div>
      )}
    </div>
  );
};

export default SegmentTimeline;
